import * as React from 'react';

import { useBreakpoint } from '../../../hooks/useBreakpoint';
import { cx } from '../../../utils/cx';

interface IEntityPaneBodyProps {
  /** Full-width content above the two columns at xl+. */
  primaryElement?: React.ReactNode;
  /** Main column at xl+. */
  leftColumn?: React.ReactNode;
  /** Side column at xl+ (347px). */
  rightColumn?: React.ReactNode;
  /** Below xl, render these in order. When omitted, order is primary, right, left. */
  singleColumnOrderOverride?: React.ReactNode[];
  className?: string;
}

/**
 * Scrollable pane body with optional primary block and two-column layout.
 * Responsive: below xl, either stacks singleColumnOrderOverride in order (if provided) or
 * shows right then left; at xl and up, shows primary then left | right.
 */
export const EntityPaneBody = ({
  primaryElement,
  leftColumn,
  rightColumn,
  singleColumnOrderOverride,
  className,
}: IEntityPaneBodyProps) => {
  const isXl = useBreakpoint('xl');
  const scrollClassName = cx(
    `flex h-full w-full flex-col gap-5 overflow-y-auto overflow-x-hidden px-4 pb-4 lg:px-8 lg:pb-8`,
    className
  );
  const hasSingleColumnOrderOverride = singleColumnOrderOverride != null && singleColumnOrderOverride.length > 0;
  const hasColumns = leftColumn != null || rightColumn != null;

  // Mobile: single-column stacked in override order (only one tree mounted)
  if (hasSingleColumnOrderOverride && !isXl) {
    return (
      <div className={scrollClassName}>
        {singleColumnOrderOverride.map((section, index) => (
          <div key={index} className="flex min-w-0 flex-col gap-5">
            {section}
          </div>
        ))}
      </div>
    );
  }

  // Desktop (or no override): primary element + two-column layout
  return (
    <div className={scrollClassName}>
      {primaryElement != null && <div className="flex flex-col gap-5">{primaryElement}</div>}
      {hasColumns && (
        <div className="flex min-w-0 flex-1 flex-col gap-5 xl:flex-row">
          {/* When no singleColumnOrderOverride, order-1/order-2 make stacked order below xl be primary → right → left (DOM is primary → left → right). */}
          {leftColumn != null && (
            <div
              className={cx(
                'flex min-w-0 flex-1 flex-col gap-5',
                !hasSingleColumnOrderOverride && 'order-2 xl:order-none'
              )}
            >
              {leftColumn}
            </div>
          )}
          {rightColumn != null && (
            <div
              className={cx(
                'flex w-full flex-col gap-5 xl:w-[347px] xl:shrink-0',
                !hasSingleColumnOrderOverride && 'order-1 xl:order-none'
              )}
            >
              {rightColumn}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface IEntityPaneProps {
  children: React.ReactNode;
  className?: string;
}

const EntityPane = ({ children, className }: IEntityPaneProps) => {
  return <div className={cx(`flex h-full w-full flex-col`, className)}>{children}</div>;
};

export default EntityPane;
